import { LegalLayout, Section, Highlight } from "./_LegalLayout";
import { SEO } from "@/lib/seo";
import { Lock, Eye, FileLock2, Server, KeyRound, AlertTriangle, RefreshCw } from "lucide-react";

const PILLARS = [
  { icon: Lock, title: "Encrypted connections", desc: "Fieldlink uses TLS-encrypted connections for supported production communications between users and our services." },
  { icon: KeyRound, title: "Secure password hashing", desc: "Fieldlink currently uses bcrypt hashing. Passwords are transmitted over encrypted connections and are not stored in plain text." },
  { icon: FileLock2, title: "Organisation isolation", desc: "Customer records are scoped to an organisation identifier, with access enforced through application-level authentication and authorisation controls." },
  { icon: Server, title: "Encryption at rest", desc: "Fieldlink currently uses MongoDB Atlas, which provides encryption at rest for supported persistent storage." },
  { icon: Eye, title: "Bot & brute-force protection", desc: "Cloudflare Turnstile helps block bot signups, and repeated failed logins are rate-limited to help protect accounts." },
  { icon: RefreshCw, title: "Backups & recovery", desc: "Backup and recovery processes are designed to support business continuity and disaster recovery." },
];

export default function Security() {
  return (
    <>
      <SEO
        title="Security · Fieldlink NZ"
        description="How Fieldlink protects your civil contracting data — encrypted connections, organisation isolation, secure password hashing, backups and monitoring, aligned to NZ privacy law."
        path="/security"
      />
      <LegalLayout
        tag="/ Security"
        title="How we protect your data"
        intro="Civil contracting involves commercially sensitive information, worker records, customer information, financial data and operational records. Fieldlink uses technical and organisational safeguards designed to protect that information against unauthorised access, disclosure, alteration, loss and misuse."
        lastUpdated="14 September 2026"
      >
        <Highlight tone="emerald">
          <strong>The short version:</strong> Fieldlink uses encrypted connections, access controls, tenant isolation, secure password hashing, backups, monitoring and other security measures designed to protect customer information. No internet-connected platform can guarantee that a security incident will never occur, but security is built into the way Fieldlink is designed and operated.
        </Highlight>

        <div className="grid sm:grid-cols-2 gap-4 my-6">
          {PILLARS.map((p) => (
            <div key={p.title} className="rounded-lg border border-[#cbd3d5] bg-white p-4">
              <p.icon className="w-6 h-6 text-[#a63c0b] mb-2" strokeWidth={2.2} />
              <div className="font-display font-bold uppercase tracking-tight text-sm">{p.title}</div>
              <p className="text-xs text-[#52616c] mt-1">{p.desc}</p>
            </div>
          ))}
        </div>

        <Section id="encryption-in-transit" title="Encryption in transit">
          <p>
            All supported connections between users and Fieldlink are protected using modern encrypted transport such as TLS.
          </p>
          <p>
            Connections between Fieldlink services and supported infrastructure providers are also configured to use encrypted transport where supported and appropriate. Fieldlink uses TLS-encrypted connections for supported production communications.
          </p>
        </Section>

        <Section id="password-security" title="Password security">
          <p>
            Passwords are not stored in plain text. Fieldlink uses secure one-way password hashing for account credentials, and currently uses <strong>bcrypt</strong> password hashing.
          </p>
          <p>
            Passwords are transmitted over encrypted connections and are not stored in plain text. Repeated failed login attempts are rate-limited per email and per IP address to help protect accounts, and account signup is protected by <strong>Cloudflare Turnstile</strong>, a privacy-respecting CAPTCHA alternative that helps block automated signups.
          </p>
        </Section>

        <Section id="tenant-isolation" title="Tenant / organisation isolation">
          <p>
            Fieldlink is a multi-tenant platform. Customer records are associated with an organisation identifier and access is restricted through application-level authentication, authorisation and organisation-scoping controls.
          </p>
          <p>
            Backend services are designed to prevent users from accessing another customer organisation&apos;s information. File access and API requests are subject to permission and organisation checks. Fieldlink continually maintains and improves these controls as the platform evolves.
          </p>
          <p>
            Access by authorised Fieldlink personnel is restricted and should occur only where reasonably necessary for support, security, maintenance, legal obligations or other legitimate operational purposes. Where practical, customer support access is logged and controlled.
          </p>
        </Section>

        <Section id="data-at-rest" title="Data at rest">
          <p>
            Fieldlink&apos;s production database infrastructure uses encryption at rest provided by its hosting and database providers. Fieldlink currently uses MongoDB Atlas for primary database infrastructure, and MongoDB Atlas provides encryption at rest for supported persistent storage.
          </p>
          <p>
            Sensitive information may also be protected through additional application or access controls where appropriate.
          </p>
        </Section>

        <Section id="data-in-transit" title="Data in transit">
          <p>
            Fieldlink production traffic is served over HTTPS/TLS. Connections to supported databases and third-party services are configured to use encrypted transport where supported and appropriate.
          </p>
          <p>
            Cloudflare may be used for network security, DDoS mitigation and traffic protection.
          </p>
        </Section>

        <Section id="backups" title="Backups and recovery">
          <p>
            Fieldlink maintains backup and recovery processes designed to support business continuity and disaster recovery. Recovery time depends on the nature and scale of the incident.
          </p>
          <p>
            Backup retention periods may vary depending on the system, data and infrastructure configuration. Backups are not a substitute for a customer&apos;s own retention requirements.
          </p>
        </Section>

        <Section id="ai" title="AI and customer data">
          <p>
            Some Fieldlink AI features may send specific information submitted by a user to third-party AI providers in order to provide the requested feature. Only the information reasonably required for that AI action should be sent.
          </p>
          <p>
            Fieldlink does not intentionally use Customer Data to train Fieldlink&apos;s own general-purpose AI models. Where Fieldlink uses commercial AI APIs, data handling is subject to Fieldlink&apos;s arrangements with those providers and their applicable enterprise/API terms.
          </p>
          <p>
            Customers should avoid entering information into AI functionality where they are not authorised to do so. See our <a href="/privacy" className="underline">Privacy Policy</a> for additional information.
          </p>
        </Section>

        <Section id="incidents" title="If a security incident occurs">
          <p>
            If Fieldlink becomes aware of a suspected security incident affecting customer information, we may:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>investigate the incident;</li>
            <li>take reasonable steps to contain it;</li>
            <li>secure affected accounts or credentials;</li>
            <li>preserve relevant logs and evidence;</li>
            <li>work with affected service providers;</li>
            <li>assess the information involved;</li>
            <li>assess the likely consequences;</li>
            <li>communicate with affected customers where appropriate; and</li>
            <li>make notifications required under applicable law.</li>
          </ul>
          <p>
            Where an incident constitutes a notifiable privacy breach under the New Zealand Privacy Act 2020, Fieldlink will follow the applicable statutory notification requirements. Fieldlink aims to communicate material incidents promptly once enough reliable information is available.
          </p>
        </Section>

        <Section id="post-incident" title="Post-incident review">
          <p>
            Following a significant incident, Fieldlink may provide affected customers with information about the incident, its impact and relevant remediation where appropriate, taking into account security, legal and confidentiality considerations.
          </p>
        </Section>

        <Section id="shared-responsibility" title="Shared security responsibility">
          <p>
            Fieldlink protects the platform and infrastructure within its control. Customers also play an important role in protecting their information. Customers should:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>use strong, unique passwords;</li>
            <li>protect account credentials;</li>
            <li>use available authentication security features;</li>
            <li>only provide access to authorised users;</li>
            <li>promptly remove users who no longer require access;</li>
            <li>review account permissions;</li>
            <li>secure devices used to access Fieldlink;</li>
            <li>protect downloaded and exported information;</li>
            <li>review third-party integrations; and</li>
            <li>promptly report suspected unauthorised access.</li>
          </ul>
          <p>
            Fieldlink cannot prevent security incidents caused solely by compromised customer-controlled devices, credentials or systems, although we may assist customers in responding to those incidents.
          </p>
        </Section>

        <Section id="service-providers" title="Service providers">
          <p>
            Like most modern cloud software, Fieldlink relies on carefully selected third-party infrastructure and service providers. Depending on the Fieldlink functionality used, providers may assist with:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>cloud infrastructure;</li>
            <li>databases;</li>
            <li>email delivery;</li>
            <li>network protection;</li>
            <li>mapping;</li>
            <li>analytics;</li>
            <li>artificial intelligence;</li>
            <li>authentication;</li>
            <li>payments;</li>
            <li>communications; and</li>
            <li>monitoring.</li>
          </ul>
          <p>
            More information about how third parties may process personal information is available in the Fieldlink <a href="/privacy" className="underline">Privacy Policy</a>.
          </p>
        </Section>

        <Section id="no-guarantee" title="No online system is risk-free">
          <p>
            Fieldlink takes security seriously and uses safeguards designed to protect customer information. However, no internet-connected service, database, network, device or cloud platform can be guaranteed to be completely secure or continuously available.
          </p>
          <p>
            Security threats change over time, and Fieldlink continually reviews and improves its safeguards. Nothing on this page limits Fieldlink&apos;s obligations under applicable law or the Fieldlink <a href="/terms" className="underline">Terms of Service</a>.
          </p>
        </Section>

        <Section id="responsible-disclosure" title="Found a security issue?">
          <p>
            If you believe you have discovered a vulnerability affecting Fieldlink, please report it responsibly to <a href="mailto:security@fieldlink.co.nz" className="underline">security@fieldlink.co.nz</a>.
          </p>
          <p>Please include:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>a description of the issue;</li>
            <li>affected URL or feature;</li>
            <li>steps required to reproduce it;</li>
            <li>screenshots or evidence where appropriate; and</li>
            <li>your contact details.</li>
          </ul>
          <p>Please do not:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>access another customer&apos;s data;</li>
            <li>alter or delete production information;</li>
            <li>perform denial-of-service testing;</li>
            <li>use automated high-volume attacks;</li>
            <li>publicly disclose an unremediated vulnerability; or</li>
            <li>intentionally exceed what is reasonably necessary to demonstrate the issue.</li>
          </ul>
          <p className="text-xs text-[#52616c] italic mt-3 flex items-start gap-1">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Fieldlink will review good-faith security reports and respond where appropriate.</span>
          </p>
        </Section>

        <Section id="contact" title="Contact">
          <p>Security: <a href="mailto:security@fieldlink.co.nz" className="underline">security@fieldlink.co.nz</a></p>
          <p>Privacy: <a href="mailto:privacy@fieldlink.co.nz" className="underline">privacy@fieldlink.co.nz</a></p>
          <p>General support: <a href="mailto:support@fieldlink.co.nz" className="underline">support@fieldlink.co.nz</a></p>
        </Section>
      </LegalLayout>
    </>
  );
}
